"use client";

import React from "react";
import { Phone, Search, Wind, CircleCheck, ArrowRight, LoaderCircle } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="32183" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 bg-chart-3/10 text-chart-3 rounded-sm flex items-center justify-center mb-2">
                    <CircleCheck className="size-8" />
                </div>
                <h3 className="text-2xl font-bold text-foreground tracking-tighter">Protection Secured!</h3>
                <p className="text-muted-foreground max-w-xs mx-auto">
                    {successMessage}
                </p>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {}
                <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider font-bold">Full Name</FormLabel>
                            <FormControl>
                                <Input placeholder="John Smith" className="rounded-sm border-border focus:ring-chart-1" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                {}
                <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider font-bold">Phone Number</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="(555) 000-0000" className="rounded-sm border-border focus:ring-chart-1" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            {}
            <FormField control={control} name="email" rules={{
      required: "Email Address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs uppercase tracking-wider font-bold">Email Address</FormLabel>
                        <FormControl>
                            <Input type="email" placeholder="jsmith@example.com" className="rounded-sm border-border focus:ring-chart-1" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {}
            <FormField control={control} name="service_type" rules={{
      required: "Please select a project type"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs uppercase tracking-wider font-bold">Project Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                                <SelectTrigger className="rounded-sm border-border focus:ring-chart-1">
                                    <SelectValue placeholder="Select a service" />
                                </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                                {["Concrete Sealing", "Stone Waterproofing", "Driveway Protection", "Pool Deck Sealing", "Industrial Coating", "Other"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                                        {option}
                                    </SelectItem>)}
                            </SelectContent>
                        </Select>
                        <FormMessage />
                    </FormItem>} />

            {}
            <FormField control={control} name="address" render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs uppercase tracking-wider font-bold">Property Address</FormLabel>
                        <FormControl>
                            <Input placeholder="123 Coastal Way, Island City" className="rounded-sm border-border focus:ring-chart-1" {...field} />
                        </FormControl>
                        <FormDescription className="text-[10px] uppercase">Required for onsite assessment scheduling.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            {}
            <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs uppercase tracking-wider font-bold">Project Details</FormLabel>
                        <FormControl>
                            <Input placeholder="Describe the surface condition..." className="rounded-sm border-border focus:ring-chart-1" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-sm text-destructive text-sm font-medium">
                    {submitError}
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-sm uppercase tracking-widest font-bold bg-chart-1 hover:bg-chart-1/90 text-white rounded-sm shadow-md transition-all duration-300">
                {isSubmitting ? <>
                        <LoaderCircle className="mr-2 size-5 animate-spin" />
                        Processing Request...
                    </> : <>
                        Request Professional Seal
                        <ArrowRight className="ml-2 size-5" />
                    </>}
            </Button>
        </Form>;
}
export default function SecureYourFreeQuote() {
  const {
    ref: stepsRef,
    isVisible: stepsVisible
  } = useRevealOnScroll();
  const steps = [{
    icon: Phone,
    title: "Consultation",
    desc: "Schedule a comprehensive on-site evaluation of your stone or concrete surfaces."
  }, {
    icon: Search,
    title: "Assessment",
    desc: "Our specialists analyze substrate conditions to determine the ideal sealing solution."
  }, {
    icon: Wind,
    title: "Application",
    desc: "We apply industry-leading sealants to ensure long-term moisture and wear resistance."
  }];
  return <section id="secure-your-surfaces" className="relative py-24 bg-accent/30 overflow-hidden">
            {}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-chart-1/5 rounded-full blur-[120px]" />
                <div className="absolute bottom-[10%] right-[5%] w-[30%] h-[30%] bg-chart-2/5 rounded-full blur-[120px]" />
            </div>

            <div className="container mx-auto px-4 relative z-10">
                <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">
                    
                    {}
                    <div className="lg:col-span-2 space-y-10 pt-4">
                        <div className="space-y-4">
                            <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-foreground leading-none">
                                Ready to <span className="text-chart-1">Protect Your Investment?</span>
                            </h2>
                            <p className="text-lg text-muted-foreground leading-relaxed font-normal">
                                Safeguard your property with professional-grade waterproofing. Our process is engineered for durability and designed for your peace of mind.
                            </p>
                        </div>

                        <div ref={stepsRef} className="space-y-8">
                            {steps.map((step, i) => <div key={i} data-index={i} className={`flex gap-5 transition-all duration-700 delay-[${i * 200}ms] ${stepsVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
                                    <div className="flex-shrink-0">
                                        <div className="size-12 rounded-sm bg-white shadow-sm border border-border flex items-center justify-center text-chart-1">
                                            <step.icon className="size-6" />
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="font-bold text-lg text-foreground uppercase tracking-tight">
                                            {i + 1}. {step.title}
                                        </h3>
                                        <p className="text-muted-foreground text-sm leading-relaxed">
                                            {step.desc}
                                        </p>
                                    </div>
                                </div>)}
                        </div>

                        <div className="hidden lg:block pt-6">
                            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-sm bg-chart-3/10 text-chart-3 text-xs font-bold uppercase tracking-widest border border-chart-3/20">
                                <CircleCheck className="size-4" />
                                <span>Professional Waterproofing Specialists</span>
                            </div>
                        </div>
                    </div>

                    {}
                    <div className="lg:col-span-3">
                        <Card className="border border-border/50 shadow-2xl shadow-chart-2/5 bg-white/60 backdrop-blur-2xl overflow-hidden rounded-sm">
                            <div className="h-1.5 w-full bg-gradient-to-r from-chart-1 via-chart-2 to-chart-1" />
                            <CardHeader className="pb-2">
                                <CardTitle className="text-2xl font-bold tracking-tighter uppercase">Request Assessment</CardTitle>
                                <CardDescription className="text-muted-foreground">
                                    Complete the form below and an Island Seals expert will respond within 24 hours.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="pt-6">
                                <FormProvider formId="free_quote_form_home" sectionName="secure-your-surfaces" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                service_type: "",
                address: "",
                details: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true
                },
                service_type: {
                  required: true
                }
              }} submitText="Request Professional Seal" successMessage="Thank you! We have received your request and will contact you shortly to schedule your surface assessment.">
                                    <QuoteForm />
                                </FormProvider>
                            </CardContent>
                        </Card>
                    </div>

                </div>
            </div>
        </section>;
}